import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import PdfViewer from '@/components/PdfViewer';
import {
  FileText, MapPin, Layers, DollarSign, Search,
  ChevronRight, Loader2, ZoomIn, ZoomOut, RotateCw, Eye
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DrawingLocationPanel({ projectId, files, initialPage, initialFile }) {
  const [pageMap, setPageMap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPage, setSelectedPage] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedPdfFile, setSelectedPdfFile] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pdfUrl, setPdfUrl] = useState('');
  const containerRef = useRef(null);

  const fetchPageMap = useCallback(async () => {
    try {
      const res = await fetch(`${API}/projects/${projectId}/takeoff/page-map`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPageMap(data);
        // Auto-select initial page if provided, otherwise select first
        if (initialPage && data.page_list.length > 0) {
          const match = data.page_list.find(p => p.includes(initialPage));
          if (match) setSelectedPage(match);
          else setSelectedPage(data.page_list[0]);
        } else if (data.page_list.length > 0) {
          setSelectedPage(data.page_list[0]);
        }
      }
    } catch (e) {}
    finally { setLoading(false); }
  }, [projectId, initialPage]);

  useEffect(() => { fetchPageMap(); }, [fetchPageMap]);

  // Helper to get tokenized PDF URL
  const getPdfUrl = async (fileId, pageHash) => {
    try {
      const res = await fetch(`${API}/projects/${projectId}/files/${fileId}/pdf-token`, { credentials: 'include' });
      const data = await res.json();
      if (data.token) return `${API}/projects/${projectId}/files/${fileId}/pdf?token=${data.token}${pageHash}`;
    } catch {}
    return `${API}/projects/${projectId}/files/${fileId}/pdf${pageHash}`;
  };

  // Extract page number from page key like "SPECTRA - PERMIT DRAWINGS.pdf / 39"
  const getPageNumber = (pageKey) => {
    if (!pageKey) return '';
    const parts = pageKey.split('/');
    const pageNum = parts[parts.length - 1]?.trim();
    // Try to extract just the number
    const match = pageNum?.match(/(\d+)/);
    return match ? match[1] : '';
  };

  // Set PDF viewer to the file matching the selected page
  useEffect(() => {
    if (!selectedPage || !pageMap || !files?.length) return;
    const pageInfo = pageMap.pages[selectedPage];
    const pageNum = getPageNumber(selectedPage);
    const pageHash = pageNum ? `#page=${pageNum}` : '';
    
    const loadPdf = async (file) => {
      setSelectedPdfFile(file);
      const url = await getPdfUrl(file.file_id, pageHash);
      setPdfUrl(url);
    };
    
    if (pageInfo?.source_file) {
      const matchFile = files.find(f => f.filename === pageInfo.source_file || f.filename?.includes(pageInfo.source_file));
      if (matchFile) { loadPdf(matchFile); return; }
    }
    if (initialFile) {
      const matchFile = files.find(f => f.filename === initialFile || f.filename?.includes(initialFile));
      if (matchFile) { loadPdf(matchFile); return; }
    }
    if (!selectedPdfFile && files.length > 0) {
      loadPdf(files[0]);
    }
  }, [selectedPage, pageMap, files, projectId, initialFile, selectedPdfFile]);

  const fmtC = (v) => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-[#1D4053] animate-spin" /></div>;

  if (!pageMap || pageMap.page_list.length === 0) {
    return (
      <Card className="border-dashed border-2 border-[#E2E8ED] shadow-none">
        <CardContent className="py-16 text-center">
          <MapPin className="w-10 h-10 text-[#1D4053]/30 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-[#0D1830] mb-1">Drawing Location Viewer</h3>
          <p className="text-sm text-[#72A4BF] max-w-md mx-auto">
            Run AI analysis on your documents first. This view shows which components were found on each page of your drawings.
          </p>
        </CardContent>
      </Card>
    );
  }

  const currentPageData = selectedPage ? pageMap.pages[selectedPage] : null;
  const filteredPages = search
    ? pageMap.page_list.filter(p => p.toLowerCase().includes(search.toLowerCase()) ||
        pageMap.pages[p].components.some(c => c.component_name.toLowerCase().includes(search.toLowerCase())))
    : pageMap.page_list;

  return (
    <div className="space-y-4" data-testid="drawing-location-panel">
      {/* Top summary */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <Card className="border-[#E2E8ED] shadow-none">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#72A4BF]/10 flex items-center justify-center"><FileText className="w-4 h-4 text-[#0D1830]" /></div>
            <div><p className="text-lg font-bold text-[#0D1830]">{pageMap.page_list.length}</p><p className="text-xs text-[#72A4BF]">Pages with Data</p></div>
          </CardContent>
        </Card>
        <Card className="border-[#E2E8ED] shadow-none">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#72A4BF]/10 flex items-center justify-center"><Layers className="w-4 h-4 text-[#0D1830]" /></div>
            <div><p className="text-lg font-bold text-[#0D1830]">{pageMap.total_components}</p><p className="text-xs text-[#72A4BF]">Total Components</p></div>
          </CardContent>
        </Card>
        {currentPageData && (
          <Card className="border-[#1D4053]/20 shadow-none bg-[#72A4BF]/10">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-[#1D4053]/20 flex items-center justify-center"><MapPin className="w-4 h-4 text-[#0D1830]" /></div>
              <div>
                <p className="text-lg font-bold gold-text">{currentPageData.component_count}</p>
                <p className="text-xs text-[#72A4BF]">On Selected Page</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Split view: Page list + Drawing/Components */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4" style={{ minHeight: '500px' }}>
        {/* Left: Page list */}
        <div className="lg:col-span-3 space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#72A4BF]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search pages..."
              className="text-xs h-8 pl-8"
              data-testid="page-search"
            />
          </div>
          <div className="space-y-1 max-h-[460px] overflow-y-auto pr-1">
            {filteredPages.map((pageKey) => {
              const pd = pageMap.pages[pageKey];
              const isSelected = selectedPage === pageKey;
              return (
                <button
                  key={pageKey}
                  onClick={() => setSelectedPage(pageKey)}
                  className={`w-full text-left p-2.5 rounded-lg border transition-all ${
                    isSelected
                      ? 'border-[#1D4053] bg-[#72A4BF]/10 shadow-sm'
                      : 'border-[#E2E8ED] bg-white hover:border-[#1D4053]/40'
                  }`}
                  data-testid={`page-btn-${pageKey}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <MapPin className={`w-3 h-3 shrink-0 ${isSelected ? 'text-[#0D1830]' : 'text-[#72A4BF]/60'}`} />
                      <span className={`text-xs font-medium truncate ${isSelected ? 'text-[#0D1830]' : 'text-[#446E87]'}`}>{pd.page_reference}</span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Badge className="bg-[#F0F4F7] text-[#446E87] border-0 text-[9px] px-1.5 py-0 rounded-full">{pd.component_count}</Badge>
                      <ChevronRight className={`w-3 h-3 ${isSelected ? 'text-[#0D1830]' : 'text-[#DDD]'}`} />
                    </div>
                  </div>
                  {pd.source_file && (
                    <p className="text-[9px] text-[#72A4BF] mt-0.5 ml-5 truncate">{pd.source_file}</p>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Center: PDF Viewer */}
        <div className="lg:col-span-5 border border-[#E2E8ED] rounded-xl bg-white overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-3 py-2 border-b border-[#E2E8ED] bg-[#F7FAFB]">
            <div className="flex items-center gap-2 overflow-x-auto">
              {(files || []).map(f => (
                <button key={f.file_id} onClick={async () => { 
                  setSelectedPdfFile(f); 
                  const pageNum = getPageNumber(selectedPage);
                  const pageHash = pageNum ? `#page=${pageNum}` : '';
                  const url = await getPdfUrl(f.file_id, pageHash);
                  setPdfUrl(url); 
                }}
                  className={`text-[10px] px-2.5 py-1 rounded-full whitespace-nowrap transition-colors ${
                    selectedPdfFile?.file_id === f.file_id ? 'bg-[#0D1830] text-white' : 'bg-white border border-[#E2E8ED] text-[#446E87] hover:border-[#1D4053]'
                  }`}
                  data-testid={`drawing-file-tab-${f.file_id}`}>
                  {f.filename?.replace('.pdf', '')}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => setZoom(z => Math.max(0.3, z - 0.2))} className="p-1 rounded hover:bg-[#72A4BF]/8"><ZoomOut className="w-3.5 h-3.5 text-[#446E87]" /></button>
              <span className="text-[10px] text-[#72A4BF] w-8 text-center">{(zoom * 100).toFixed(0)}%</span>
              <button onClick={() => setZoom(z => Math.min(3, z + 0.2))} className="p-1 rounded hover:bg-[#72A4BF]/8"><ZoomIn className="w-3.5 h-3.5 text-[#446E87]" /></button>
              <button onClick={() => setZoom(1)} className="p-1 rounded hover:bg-[#72A4BF]/8"><RotateCw className="w-3 h-3 text-[#446E87]" /></button>
            </div>
          </div>
          <div className="flex-1 overflow-auto bg-[#E8E8E6]" ref={containerRef}>
            {pdfUrl ? (
              <div style={{ transform: `scale(${zoom})`, transformOrigin: 'top left', transition: 'transform 0.2s' }}>
                <PdfViewer
                  url={pdfUrl.split('#')[0]}
                  initialPage={parseInt(getPageNumber(selectedPage)) || 1}
                  scale={1}
                  className="min-h-[70vh]"
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-[#72A4BF] text-sm py-20">
                <div className="text-center">
                  <FileText className="w-8 h-8 text-[#1D4053]/30 mx-auto mb-2" />
                  <p className="text-xs">Select a page to view its drawing</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right: Components on this page */}
        <div className="lg:col-span-4 border border-[#E2E8ED] rounded-xl bg-white overflow-hidden flex flex-col">
          <div className="px-3 py-2.5 border-b border-[#E2E8ED] bg-[#F7FAFB]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Eye className="w-3.5 h-3.5 text-[#0D1830]" />
                <span className="text-xs font-semibold text-[#0D1830]">
                  {currentPageData ? `Components on ${currentPageData.page_reference}` : 'Select a Page'}
                </span>
              </div>
              {currentPageData && (
                <span className="text-xs font-semibold gold-text">{fmtC(currentPageData.total_cost)}</span>
              )}
            </div>
          </div>
          <div className="flex-1 overflow-auto">
            {currentPageData ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E2E8ED]">
                    <TableHead className="text-[10px]">Component</TableHead>
                    <TableHead className="text-[10px] text-right">Qty</TableHead>
                    <TableHead className="text-[10px] text-right">Cost</TableHead>
                    <TableHead className="text-[10px]">Div</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentPageData.components.map((comp, i) => (
                    <TableRow key={i} className="border-[#F0F4F7]" data-testid={`page-comp-${i}`}>
                      <TableCell className="text-[10px] font-medium max-w-[140px]">
                        <p className="truncate">{comp.component_name}</p>
                        <p className="text-[9px] text-[#72A4BF] truncate">{comp.assembly_name}</p>
                      </TableCell>
                      <TableCell className="text-[10px] text-right whitespace-nowrap">{comp.quantity} {comp.unit}</TableCell>
                      <TableCell className="text-[10px] text-right font-medium">{fmtC(comp.total_cost)}</TableCell>
                      <TableCell className="text-[10px]">
                        <Badge className="bg-[#F0F4F7] text-[#446E87] border-0 text-[8px] px-1 py-0 rounded">{comp.division_code}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="flex items-center justify-center h-full p-6 text-center">
                <div>
                  <MapPin className="w-6 h-6 text-[#1D4053]/30 mx-auto mb-2" />
                  <p className="text-xs text-[#72A4BF]">Select a page from the left to see components found on that drawing.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
